import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

export default function ExchangeDealerForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [formData, setFormData] = useState({
        dealer_name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        is_active: true
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (id) loadDealer();
    }, [id]);

    const loadDealer = async () => {
        try {
            const response = await api.get(`/exchange_dealers/${id}`);
            setFormData(response.data.data);
        } catch (error) {
            console.error('Error loading dealer:', error);
            toast.error('Failed to load dealer details');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        try {
            let response;
            if (id) {
                response = await api.put(`/exchange_dealers/${id}`, { exchange_dealer: formData });
            } else {
                response = await api.post('/exchange_dealers', { exchange_dealer: formData });
            }

            if (response.success) {
                toast.success(`Dealer ${id ? 'updated' : 'created'} successfully`);
                navigate('/exchange_dealers');
            } else {
                const msg = response.errors?.length > 0 ? response.errors[0] : (response.message || response.error || 'Error saving dealer');
                toast.error(msg);
            }
        } catch (error: any) {
            console.error('Error saving dealer:', error);
            toast.error(error.message || 'An unexpected error occurred');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <h1 className="text-3xl font-black text-slate-900 mb-8">
                {id ? 'Edit' : 'New'} Exchange Dealer
            </h1>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            Dealer Name <span className="text-rose-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.dealer_name}
                            onChange={(e) => setFormData({ ...formData, dealer_name: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-slate-100 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                            placeholder="e.g., Old Bike Bazaar"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Contact Person</label>
                        <input
                            type="text"
                            value={formData.contact_person}
                            onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-slate-100 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Phone</label>
                        <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-slate-100 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-slate-100 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Status</label>
                        <select
                            value={formData.is_active ? 'true' : 'false'}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                            className="w-full px-4 py-3 border-2 border-slate-100 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                        >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-bold text-slate-700 mb-2">Address</label>
                        <textarea
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-3 border-2 border-slate-100 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all resize-none"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={() => navigate('/exchange_dealers')}
                        className="px-6 py-3 text-slate-600 font-bold hover:bg-slate-100 rounded-xl transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-8 py-3 bg-indigo-600 text-white font-black uppercase tracking-widest rounded-xl hover:bg-indigo-700 transition-all disabled:opacity-50 text-sm shadow-lg hover:shadow-xl"
                    >
                        {saving ? 'Saving...' : 'Save Dealer'}
                    </button>
                </div>
            </form>
        </div>
    );
}
